import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import type { ActiveSessionDto, Paginated, ServiceKind, SessionDto } from '../api/types';
import { useAuth } from '../auth/auth-context';
import { ServiceBadge } from '../components/status';
import { Card, ConfirmDialog, Empty, ErrorAlert, Pagination, Spinner } from '../components/ui';
import { useCurrentRouter } from '../lib/current-router';
import { formatBytes, formatDateTime, formatDuration } from '../lib/format';

export function SessionsPage() {
  const [tab, setTab] = useState<'active' | 'history'>('active');

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Sesiones</h1>
          <p>
            «Activas» consulta el router en vivo. El histórico lo materializa un trabajo periódico,
            porque RouterOS olvida una sesión al cerrarse.
          </p>
        </div>
      </div>

      <div className="toolbar">
        <button
          type="button"
          className={tab === 'active' ? 'primary' : ''}
          onClick={() => setTab('active')}
        >
          Activas ahora
        </button>
        <button
          type="button"
          className={tab === 'history' ? 'primary' : ''}
          onClick={() => setTab('history')}
        >
          Histórico
        </button>
      </div>

      {tab === 'active' ? <ActiveSessions /> : <SessionHistory />}
    </main>
  );
}

function ActiveSessions() {
  const { canOnRouter } = useAuth();
  const queryClient = useQueryClient();
  const { routerId } = useCurrentRouter();

  const [disconnecting, setDisconnecting] = useState<ActiveSessionDto | null>(null);
  const [error, setError] = useState<unknown>(null);

  const sessions = useQuery({
    queryKey: ['sessions', 'active', routerId],
    enabled: Boolean(routerId),
    queryFn: () => api.get<ActiveSessionDto[]>('/sessions/active', { routerId }),
    refetchInterval: 15_000,
  });

  const disconnect = useMutation({
    mutationFn: (session: ActiveSessionDto) =>
      api.del<void>(
        `/sessions/active/${session.routerId}/${encodeURIComponent(session.id)}?service=${session.service}`,
      ),
    onSuccess: () => {
      setDisconnecting(null);
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (caught) => setError(caught),
  });

  return (
    <>
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      <div className="toolbar">
        <button type="button" className="small" onClick={() => void sessions.refetch()}>
          Actualizar
        </button>
        {sessions.isFetching && <Spinner label="" />}
        <span className="muted">Se refresca cada 15 s</span>
      </div>

      <Card>
        {sessions.isLoading ? (
          <div className="card-body">
            <Spinner />
          </div>
        ) : sessions.data && sessions.data.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Servicio</th>
                  <th>IP</th>
                  <th className="num">Conectado</th>
                  <th className="num">Consumo</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {sessions.data.map((session) => (
                  <tr key={`${session.routerId}-${session.service}-${session.id}`}>
                    <td>
                      <strong className="mono">{session.username}</strong>
                      <div className="faint" style={{ fontSize: 12 }}>
                        {session.planName ?? session.macAddress ?? ''}
                      </div>
                    </td>
                    <td>
                      <ServiceBadge service={session.service} />
                    </td>
                    <td className="mono">{session.ipAddress ?? '—'}</td>
                    <td className="num">
                      {formatDuration(session.uptimeSeconds)}
                      {session.sessionTimeLeftSeconds > 0 && (
                        <div className="faint" style={{ fontSize: 12 }}>
                          quedan {formatDuration(session.sessionTimeLeftSeconds)}
                        </div>
                      )}
                    </td>
                    <td className="num">
                      {session.service === 'PPPOE' ? (
                        // `/ppp/active` no expone contadores de tráfico por sesión.
                        <span className="faint">—</span>
                      ) : (
                        formatBytes(session.bytesIn + session.bytesOut)
                      )}
                    </td>
                    <td className="actions">
                      {canOnRouter(session.routerId, 'delete') && (
                        <button type="button" className="small danger" onClick={() => setDisconnecting(session)}>
                          Desconectar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>No hay nadie conectado en este momento.</Empty>
        )}
      </Card>

      {disconnecting && (
        <ConfirmDialog
          title={`Desconectar ${disconnecting.username}`}
          danger
          busy={disconnect.isPending}
          confirmLabel="Desconectar"
          message={
            <p>
              Se corta la sesión en curso. La credencial sigue siendo válida y el cliente puede
              volver a entrar: para impedirlo, bloquéala desde{' '}
              {disconnecting.service === 'PPPOE' ? 'Clientes PPPoE' : 'Usuarios HotSpot'}.
            </p>
          }
          onCancel={() => setDisconnecting(null)}
          onConfirm={() => disconnect.mutate(disconnecting)}
        />
      )}
    </>
  );
}

function SessionHistory() {
  const { routerId } = useCurrentRouter();
  const [service, setService] = useState<ServiceKind | ''>('');
  const [username, setUsername] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [page, setPage] = useState(1);

  // Otro router, otro histórico: la página actual deja de tener sentido.
  useEffect(() => setPage(1), [routerId]);

  const history = useQuery({
    queryKey: ['sessions', 'history', { routerId, service, username, onlyOpen, page }],
    enabled: Boolean(routerId),
    queryFn: () =>
      api.get<Paginated<SessionDto>>('/sessions', {
        routerId,
        service: service || undefined,
        username: username || undefined,
        onlyOpen: onlyOpen || undefined,
        page,
        pageSize: 30,
      }),
  });

  return (
    <>
      <div className="toolbar">
        <select
          value={service}
          onChange={(event) => {
            setService(event.target.value as ServiceKind | '');
            setPage(1);
          }}
          style={{ width: 'auto' }}
        >
          <option value="">Ambos servicios</option>
          <option value="HOTSPOT">HotSpot</option>
          <option value="PPPOE">PPPoE</option>
        </select>

        <input
          value={username}
          onChange={(event) => {
            setUsername(event.target.value);
            setPage(1);
          }}
          placeholder="Filtrar por usuario…"
          style={{ width: 220 }}
        />

        <label className="inline">
          <input
            type="checkbox"
            checked={onlyOpen}
            onChange={(event) => {
              setOnlyOpen(event.target.checked);
              setPage(1);
            }}
          />
          Sólo sin cerrar
        </label>

        {history.isFetching && <Spinner label="" />}
      </div>

      <Card
        footer={
          history.data && history.data.meta.total > 0 ? (
            <Pagination
              page={history.data.meta.page}
              totalPages={history.data.meta.totalPages}
              total={history.data.meta.total}
              onPage={setPage}
            />
          ) : undefined
        }
      >
        {history.isLoading ? (
          <div className="card-body">
            <Spinner />
          </div>
        ) : history.data && history.data.data.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Servicio</th>
                  <th>IP</th>
                  <th>Inicio</th>
                  <th className="num">Duración</th>
                  <th className="num">Consumo</th>
                  <th>Fin</th>
                </tr>
              </thead>
              <tbody>
                {history.data.data.map((session) => (
                  <tr key={session.id}>
                    <td className="mono">{session.username}</td>
                    <td>
                      <ServiceBadge service={session.service} />
                    </td>
                    <td className="mono">{session.ipAddress ?? '—'}</td>
                    <td>{formatDateTime(session.startedAt)}</td>
                    <td className="num">{formatDuration(session.uptimeSeconds)}</td>
                    <td className="num">{formatBytes(session.bytesIn + session.bytesOut)}</td>
                    <td>
                      {session.endedAt ? (
                        <>
                          {formatDateTime(session.endedAt)}
                          {session.terminateCause && (
                            <div className="faint" style={{ fontSize: 12 }}>
                              {session.terminateCause}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="badge ok">abierta</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>No hay sesiones registradas con esos filtros.</Empty>
        )}
      </Card>
    </>
  );
}
